import React, { useEffect, useState } from "react";
import Text from "../../../../components/Text/Text";
import { icon } from "../../../../resource";
import { Col, Row, Space } from "antd";
import Input from "../../../../components/Input/Input";
import { SearchOutlined } from "@ant-design/icons";
import Select from "../../../../components/Select/Select";
import { numberFormatter } from "../../../../utility/Formatter";
import { type shopDetailOrderEntity } from "../../../../entities/AchievementEntity";
import { FlexCol } from "../../../../components/Container/Container";
import dayjs from "dayjs";
import StatusBadge from "../../../../components/Badge/StatusBadge";
import color from "../../../../resource/color";
import SortFieldCustom from "../../../../components/SortFieldTable/SortFieldCustom";
import ExpandTable from "../../../../components/Table/ExpandTable";
import Pagination from "../../../../components/Pagination/Pagination";
import styled from "styled-components";
import Link from "antd/lib/typography/Link";
import { useNavigate } from "react-router-dom";
import { getOrdersNO } from "../../../../datasource/OrderDatasourc";
import { getShipmentOrder } from "../../../../datasource/ShipmentOrderDatasource";
const StyledExpandTable = styled(ExpandTable)`
  tr.ant-table-expanded-row > td {
    padding: 8px 0 !important;
    background-color: #f3f3f3ff !important;
  }

  tr.ant-table-expanded-row .ant-table-cell {
    padding: 8px 0 !important;
    border: none !important;
    background-color: #f3f3f3ff !important;
  }
  tr.ant-table-expanded-row td:first-child {
    padding-left: 16% !important;
  }
  .ant-table-content {
    overflow: visible !important;
  }
`;
interface HoverLinkProps {
  color?: string;
  size?: string;
}
const HoverLink = styled(Link)<HoverLinkProps>`
  && {
    font-family: "Sarabun";
    color: ${({ color }) => color || "#464E5F"} !important;
    font-weight: 400;
    font-size: ${({ size }) => size || "16px"};
    transition: color 0.2s ease;

    &:hover {
      color: #1677ff !important;
      text-decoration: underline;
    }
  }
`;

interface OrderTabProps {
  data: { data: shopDetailOrderEntity[]; count: number };
  sortField: string | undefined;
  sortDirection: "ASC" | "DESC" | undefined;
  handleSortChange: (field: string) => void;
  searchText: string | undefined;
  setSearchText: (val: string | undefined) => void;
  status: string | undefined;
  setStatus: (val: string | undefined) => void;
  page: number;
  setPage: (val: number) => void;
  loading: boolean;
}

function OrderTab({
  setPage,
  page,
  data,
  sortField,
  sortDirection,
  handleSortChange,
  searchText,
  setStatus,
  status,
  setSearchText,
  loading,
}: OrderTabProps) {
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const userProfile = JSON.parse(localStorage.getItem("profile")!);

  const navigate = useNavigate();
  const columnWidth = [120, 130, 240, 90, 90, 120];
  const onChangePage = async (id: string, page: "SO" | "PDO", customerCompanyId?: string) => {
    try {
      if (!id) {
        console.warn("No ID provided for onChangePage.");
        return;
      }

      let targetUrl: string | null = null;

      if (page === "SO") {
        const res = await getOrdersNO(id);
        if (res?.orderId) {
          targetUrl = `/order/${res.orderId}`;
        } else {
          console.warn("Order ID not found in getOrdersNO response:", res);
        }
      } else {
        const res = await getShipmentOrder({
          company: userProfile?.company,
          searchText: id,
          customerCompanyId: customerCompanyId,
        });
        if (res.data?.length > 0) {
          targetUrl = `/ReceiveGoods/${res.data[0].shipmentOrderId}`;
        } else {
          console.warn("Shipment order ID not found in getShipmentOrderByOrder response:", res);
        }
      }

      if (targetUrl) {
        navigate(targetUrl);
      }
    } catch (error) {
      console.error("Error in onChangePage:", error);
    }
  };

  const columns: any = [
    {
      title: "เลขที่สั่งขาย (SO)",
      dataIndex: "soNo",
      key: "soNo",
      width: columnWidth[0],
      render: (value: any, row: any, index: number) => {
        return (
          <FlexCol>
            <HoverLink onClick={() => onChangePage(row.orderNo, "SO")} target='_blank'>
              {value || "-"}
            </HoverLink>
            <Text level={6} color='Text3'>
              {dayjs(row?.createAt).format("DD/MM/YYYY, HH:mm")}
            </Text>
          </FlexCol>
        );
      },
    },
    {
      title: "เลขที่ใบส่งของ (PDO)",
      dataIndex: "pdoNo",
      key: "pdoNo",
      width: columnWidth[1],
      render: (value: any, row: any, index: number) => {
        return (
          <FlexCol>
            <HoverLink
              onClick={() => onChangePage(value, "PDO", row.customerCompanyId)}
              target='_blank'
            >
              {value || "-"}
            </HoverLink>
            <Text fontSize={14} color='Text3'>
              {row.cnNo}
            </Text>
          </FlexCol>
        );
      },
    },
    {
      title: () => (
        <SortFieldCustom
          title='จำนวนรายการสินค้า'
          sortDirection={sortField === "productCount" ? sortDirection : undefined}
          onSortChange={() => handleSortChange("productCount")}
        />
      ),
      dataIndex: "productCount",
      key: "productCount",
      width: columnWidth[2],
      render: (value: any, row: any, index: number) => {
        return (
          <FlexCol>
            <Text>{numberFormatter(value, 0)}</Text>
            <Text fontFamily='Sarabun' level={6} color='Text3'>
              รายการ
            </Text>
          </FlexCol>
        );
      },
    },
    {
      title: () => (
        <SortFieldCustom
          title='จำนวน'
          sortDirection={sortField === "quantity" ? sortDirection : undefined}
          onSortChange={() => handleSortChange("quantity")}
        />
      ),
      dataIndex: "quantity",
      key: "quantity",
      width: columnWidth[3],
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <>
              <Text>{numberFormatter(value, 0)}</Text>
              <br />
              <Text fontFamily='Sarabun' level={6} color='Text3'>
                หน่วย
              </Text>
            </>
          ),
        };
      },
    },
    {
      title: "สถานะ",
      dataIndex: "action",
      key: "action",
      width: columnWidth[4],
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <>
              <StatusBadge
                borderRadius='12px'
                label={value === "DECREASE" ? "ยกเลิกยอด" : "ได้รับยอด"}
                bgColor={value === "DECREASE" ? color.error : color.success}
              />
            </>
          ),
        };
      },
    },
    {
      title: () => (
        <SortFieldCustom
          title='จำนวนเงิน'
          sortDirection={sortField === "amount" ? sortDirection : undefined}
          onSortChange={() => handleSortChange("amount")}
        />
      ),
      dataIndex: "amount",
      key: "amount",
      width: columnWidth[5],
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <>
              <Text>{numberFormatter(value, 2)}</Text>
              <br />
              <Text fontFamily='Sarabun' level={6} color='Text3'>
                บาท
              </Text>
            </>
          ),
        };
      },
    },
  ];
  const expandedRowRender = (record: any) => {
    const expandColumns: any[] = [
      {
        title: "สินค้า",
        dataIndex: "productName",
        key: "productName",
        width: columnWidth[1] + columnWidth[2] + 200,
        render: (value: any, row: any, index: number) => {
          return {
            children: (
              <div style={{ display: "flex", alignItems: "center" }}>
                <img
                  src={row.productImage || icon.emptyProduct}
                  style={{ width: "50px", height: "50px", objectFit: "cover", marginRight: "10px" }}
                />
                <Space direction='vertical' size={0}>
                  <Text fontFamily='Sarabun'>{value}</Text>
                  <Text fontFamily='Sarabun' fontSize={14} color='Text3'>
                    {row.productCommonName}
                  </Text>
                </Space>
              </div>
            ),
          };
        },
      },
      {
        title: "ขนาด",
        dataIndex: "packSize",
        key: "packSize",
        width: columnWidth[2],
        render: (value: any, row: any, index: number) => {
          return {
            children: (
              <Space direction='vertical' size={0}>
                <Text>{value}</Text>
                <Text fontFamily='Sarabun' fontSize={14} color='Text3'>
                  {row.productCodeNAV}
                </Text>
              </Space>
            ),
          };
        },
      },
      {
        title: "จำนวน",
        dataIndex: "quantity",
        key: "quantity",
        width: columnWidth[1] + columnWidth[2] + columnWidth[1],
        render: (value: any, row: any, index: number) => {
          return {
            children: (
              <Space direction='vertical' size={0}>
                <Text>{numberFormatter(value, 2)}</Text>
                <Text fontFamily='Sarabun' fontSize={14} color='Text3'>
                  {row.unitName}
                </Text>
              </Space>
            ),
          };
        },
      },
      {
        title: "จำนวนเงิน",
        dataIndex: "amount",
        key: "amount",
        width: columnWidth[1] + columnWidth[2],
        render: (value: any, row: any, index: number) => {
          return {
            children: (
              <Space direction='vertical' size={0}>
                <Text>{numberFormatter(value, 2)}</Text>
                <Text fontFamily='Sarabun' fontSize={14} color='Text3'>
                  บาท
                </Text>
              </Space>
            ),
          };
        },
      },
    ];

    const expandDataSource = record.productList.map((product: any, index: number) => ({
      key: index.toString(),
      productImage: product.productImage,
      productName: product.productName,
      productCodeNAV: product.productCodeNAV,
      packSize: product.packSize,
      productCommonName: product.productCommonName,
      saleUOM: product.saleUOM,
      unitName: product.unitName,
      quantity: product.quantity,
      amount: product.amount,
    }));

    return (
      <StyledExpandTable
        showHeader={false}
        expandRowBackgroundColor={"#FAFAFA"}
        columns={expandColumns}
        data={expandDataSource || []}
        loading={false}
        size='small'
      />
    );
  };

  const handleExpand = (expanded: boolean, record: any) => {
    setExpandedRowKeys(expanded ? [record.orderAchievementId.toString()] : []);
  };
  const [inputText, setInputText] = useState<string>("");
  const handleSearch = () => {
    setSearchText(inputText || undefined);
    setPage(1);
  };
  useEffect(() => {
    if (searchText === undefined) {
      setInputText("");
    }
  }, [searchText]);
  return (
    <>
      <Row gutter={10} style={{ margin: "22px 0px" }}>
        <Col span={6}>
          <Input
            allowClear
            placeholder='เลขที่สั่งขาย'
            suffix={<SearchOutlined />}
            value={inputText}
            onPressEnter={handleSearch}
            onChange={(e) => {
              const value = e.target.value;
              setInputText(value);
              if (!value) setSearchText(undefined);
            }}
          />
        </Col>
        <Col span={4}>
          <Select
            data={[
              { label: "สถานะทั้งหมด", value: "", key: "" },
              { label: "ได้รับยอด", value: "INCREASE", key: "INCREASE" },
              { label: "ยกเลิกยอด", value: "DECREASE", key: "DECREASE" },
            ]}
            value={status}
            placeholder='สถานะทั้งหมด'
            onChange={(e) => {
              setStatus(e);
              setPage(1);
            }}
          />
        </Col>
        <Col span={4} style={{ marginLeft: "auto", textAlign: "right" }}>
          <Text fontSize={14}>ทั้งหมด {numberFormatter(data.count, 0)} ร้าน</Text>
        </Col>
      </Row>
      <StyledExpandTable
        columns={columns}
        loading={loading}
        data={data?.data || []}
        imgEmpty={icon.emptyHistoryPoint}
        textEmpty='ไม่มีรายการคำสั่งซื้อ'
        expandable={{
          expandedRowRender,
          expandedRowKeys,
          onExpand: handleExpand,
        }}
        rowKey='achievementTransactionsId'
      />
      {data.count > 10 && (
        <div style={{ margin: "18px 0px" }}>
          <Pagination
            currentPage={page}
            total={data?.count || 0}
            pageSize={10}
            onPageChange={(pageNumber) => setPage(pageNumber)}
          />
        </div>
      )}
    </>
  );
}

export default OrderTab;
